import React, { useEffect, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-draw";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import ThinFooter from "../Footer/ThinFooter";
import ContentWrapper from "../Atomics/ContentWrapper/ContentWrapper";
import axiosInstance from "../../axiosInstance";
import { useParams, useHistory } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Spinner from "react-bootstrap/Spinner";
import "./edit-geo-fence.css";

const EditRaceTrack = () => {
  const { id } = useParams(); // Get track ID from route parameters
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trackData, setTrackData] = useState({
    trackName: "",
    address: "",
    mapImage: null,
  });
  const [coordinates, setCoordinates] = useState(null); // For map
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    fetchTrackDetails();
  }, [id]);

  const fetchTrackDetails = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axiosInstance.get(`/track/${id}`, {
        headers: {
          Authorization: token,
        },
      });
      if (response.status === 200 && response.data.success) {
        const { trackName, address, mapImage, coordinates } = response.data.data;
        setTrackData({
          trackName,
          address,
          mapImage: mapImage || null,
        });
        setCoordinates(coordinates);
        setError(null);
      } else {
        setError(response.data.error?.message || "Track not found.");
      }
    } catch (err) {
      setError("Failed to fetch track details. Please try again.");
      console.error("Fetch Track Details Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTrackData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) handleImageUpload(file);
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    const maxSizeInKB = 500; // 500KB limit
    const fileSizeInKB = file.size / 1024;

    if (fileSizeInKB > maxSizeInKB) {
      toast.error(`File size exceeds the maximum limit of ${maxSizeInKB}KB.`);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await fetch("https://beta.autotrak.ai/api/aws/upload", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();
      const s3Url = data.data.url;

      setTrackData((prevData) => ({
        ...prevData,
        mapImage: s3Url,
      }));

      toast.success("Image uploaded successfully!");
    } catch (error) {
      console.error("Failed to upload image:", error);
      toast.error("Failed to upload the image. Please try again.");
    }
  };

  const handleSubmit = async (e) => {
  e.preventDefault(); // Prevent default form submission behavior

  const token = localStorage.getItem("token");

  // Prepare data for the API
  const trackDataToSend = {
    trackName: trackData.trackName,
    address: trackData.address,
    ...(trackData.mapImage && { mapImage: trackData.mapImage }),
  };

  try {
    // Debug the payload
    console.log("Sending update payload:", trackDataToSend);

    const response = await axiosInstance.put(`/track/${id}`, trackDataToSend, {
      headers: {
        Authorization: token,
        "Content-Type": "application/json", // Assuming JSON payload
      },
    });

    if (response.status === 200 && response.data.success) {
      toast.success("Track updated successfully!");
      history.push("/track"); // Redirect to the tracks list
    } else {
      console.error("Update failed:", response.data);
      toast.error(response.data.error?.message || "Failed to update track.");
    }
  } catch (err) {
    console.error("Error updating track:", err.response || err);
    toast.error(
      err.response?.data?.error?.message || "Error occurred while updating track."
    );
  }
};


  const handleAddressChange = async (e) => {
    const address = e.target.value;
    setTrackData((prevDetails) => ({
      ...prevDetails,
      address,
    }));

    if (address.trim()) {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
            address
          )}&format=json&addressdetails=1&limit=5`
        );
        const data = await response.json();

        if (data && data.length > 0) {
          setSuggestions(data);
        } else {
          setSuggestions([]);
        }
      } catch (error) {
        console.error("Error fetching location data:", error);
        setSuggestions([]);
      }
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    const { display_name, lat, lon } = suggestion;

    setTrackData((prevDetails) => ({
      ...prevDetails,
      address: display_name,
    }));
    setSuggestions([]);
    setCoordinates({ lat, lon });
  };

  const renderMap = () => {
    if (coordinates) {
      const map = L.map("map").setView([coordinates.lat, coordinates.lon], 13);

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }).addTo(map);

      L.marker([coordinates.lat, coordinates.lon])
        .addTo(map)
        .bindPopup(trackData.trackName)
        .openPopup();
    }
  };

  useEffect(() => {
    if (coordinates) renderMap();
  }, [coordinates]);

  if (loading) {
    return (
      <Container>
        <Row className="text-center w-100" style={{ height: "100vh", justifyContent: "center", alignItems: "center" }}>
          <Spinner animation="border" />
        </Row>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Row className="text-center w-100" style={{ height: "100vh", justifyContent: "center", alignItems: "center" }}>
          <h4>{error}</h4>
        </Row>
      </Container>
    );
  }

  return (
    <>
      <ContentWrapper>
        <Container fluid>
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item href="/tracks">Tracks</Breadcrumb.Item>
            <Breadcrumb.Item active>Edit Track</Breadcrumb.Item>
          </Breadcrumb>
        
          <form id="ed_trck" onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <div className="form-group">
                  <label>Track Name</label>
                  <input
                    type="text"
                    className="form-control"
                    name="trackName"
                    value={trackData.trackName}
                    onChange={handleInputChange}
                    placeholder="Enter Track Name"
                    required
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="form-group">
                  <label>Map Image</label>
                  <input
                    type="file"
                    className="form-control"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                  {trackData.mapImage && !(trackData.mapImage instanceof File) && (
                    <img
                      src={trackData.mapImage}
                      alt="Track Map"
                      style={{ marginTop: "10px", width: "100px", height: "auto" }}
                    />
                  )}
                </div>
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    className="form-control"
                    name="address"
                    value={trackData.address}
                    onChange={handleAddressChange}
                    placeholder="Enter Track Address"
                    required
                  />
                  {suggestions.length > 0 && (
                    <ul className="suggestions-list">
                      {suggestions.map((suggestion, index) => (
                        <li
                          key={index}
                          onClick={() => handleSuggestionClick(suggestion)}
                        >
                          {suggestion.display_name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Col>
            </Row>
            
            <Row className="mt-4">
              <Col xs={12} className="text-right">
                <button type="submit" className="btn btn-primary sv_btn">
                  Update Track
                </button>
              </Col>
            </Row>
          </form>
        </Container>
        <ThinFooter />
      </ContentWrapper>
      <Toaster />
    </>
  );
};

export default EditRaceTrack;
